/**
 * New-workflow contracts — `/agents/workflows/new` (RO-3-02b).
 *
 * Port of the three server actions the two builders on that page called:
 * `proposeLoopAction` (text → a proposed pipeline, optionally refined by a local
 * `claude -p` pass), `createLoopFromBuilderAction` (the guided builder — seeds a
 * COMPLETE loop through `seedLoopTemplate`) and `createLoopAction` (the quick
 * form — lands a bare row through `emitLoopCreated`).
 *
 * Both create paths are kept, and kept SEPARATE, because they are not the same
 * write: one produces loop + steps + a v1 active variant, the other produces a
 * loop row with no steps. Collapsing them would silently change what the quick
 * form does.
 *
 * `LoopProposal` / `ObjectivePreset` / `GatePolicy` are NOT re-declared here.
 * They live in `excellent/src/optimization/builder-shared.ts`, which exists
 * precisely because both sides of the boundary need them and it is pure
 * (types + plain constants; its own header explains the node:fs hazard it was
 * split out of). `packages/ui` cannot import from `excellent/src`, so the wire
 * shape below is structural and the client re-uses the shared module's types at
 * the call site — one source for the constants, no fork.
 */

import { z } from "zod";
import type { Operation } from "../../types";
// Value import — `invalidates` names the target Operation objects themselves.
import { workflowFleet } from "./fleet";
import { loopsRunnability } from "./runnability";

/** Mirror of `LoopStepTemplate` (`@excellent/core`) as it crosses this wire. */
const stepTemplate = z.object({
  name: z.string().min(1),
  role: z.string().optional(),
  rung: z.number().int().min(1).max(5).optional(),
  model: z.string().optional(),
});

/** Structural mirror of `LoopProposal` in `optimization/builder-shared.ts`. */
export const loopProposalSchema = z.object({
  title: z.string(),
  domain: z.string(),
  description: z.string(),
  steps: z.array(stepTemplate),
  objectivePreset: z.enum(["cost", "balanced", "quality"]),
  objective: z.record(z.unknown()),
  gate_policy: z.enum(["ship-verify", "human", "auto-eval"]),
  rationale: z.string(),
  source: z.enum(["preset", "llm-refined"]),
});
export type LoopProposalDto = z.infer<typeof loopProposalSchema>;

const proposeBody = z.object({ description: z.string() });

/**
 * MUTATION-shaped but WRITES NOTHING — it composes a proposal and may shell a
 * local `claude -p` to refine it. POST because the description travels in a
 * body. Its authz descriptor is therefore `null`, with that reasoning recorded
 * where the descriptor lives, not here.
 *
 * NO `invalidates`, deliberately (RO-5-02): `proposeLoopAction` had no
 * `revalidatePath` because it changes no persisted state, so there is no read to
 * make stale. This is the shape `check-invalidation-declared` (RO-5-03) has to
 * tolerate — a write-nothing POST — or the gate will demand a false declaration
 * here. See the RO-5-02 map for the full list of such routes.
 */
// invalidation-ignore: write-nothing POST — composes a proposal only (RO-5-03)
const proposeOp: Operation<
  void,
  void,
  z.infer<typeof proposeBody>,
  { ok: true; proposal: LoopProposalDto } | { ok: false; error: string }
> = {
  method: "POST",
  path: () => "/api/workflows/propose",
  body: proposeBody,
  response: z.custom<{ ok: true; proposal: LoopProposalDto } | { ok: false; error: string }>(),
};

const createResult = z.custom<{ ok: boolean; slug?: string; error?: string }>();

const createFromBuilderBody = z.object({
  title: z.string(),
  domain: z.string().optional(),
  description: z.string().optional(),
  objectivePreset: z.enum(["cost", "balanced", "quality"]).optional(),
  objective: z.record(z.unknown()).optional(),
  gate_policy: z.enum(["ship-verify", "human", "auto-eval"]).optional(),
  steps: z.array(stepTemplate),
  activate: z.boolean().optional(),
});
export type CreateFromBuilderBody = z.infer<typeof createFromBuilderBody>;

const createFromBuilderOp: Operation<
  void,
  void,
  CreateFromBuilderBody,
  { ok: boolean; slug?: string; error?: string }
> = {
  method: "POST",
  path: () => "/api/workflows/from-builder",
  body: createFromBuilderBody,
  response: createResult,
  // `createLoopFromBuilderAction` → `revalidatePath("/agents/workflows")`, i.e.
  // the fleet console — a new row appears. Bare references: both targets take no
  // path params, so every `range` variant of the fleet list refetches.
  //
  // Runnability is named too, and for a sharper reason than "a loop was added":
  // this path seeds loop + STEPS, and each step carries a `role`. A seeded role
  // that is not in `knownRoleSlugs()` lands the new loop in `blocked` with its
  // role in `blocked_roles`, so both scalars move, not just `total`. (The quick
  // form below creates no steps, so only `total` moves — same declaration, but
  // it is worth knowing they are not stale for the same reason.)
  invalidates: [workflowFleet.list, loopsRunnability.get],
};

const createBody = z.object({
  title: z.string(),
  domain: z.string().optional(),
  description: z.string().optional(),
  /** The objective editor's emitted JSON STRING — validated server-side. */
  objective: z.string().optional(),
  gate_policy: z.enum(["ship-verify", "human", "auto-eval"]).optional(),
  activate: z.boolean().optional(),
});
export type CreateWorkflowBody = z.infer<typeof createBody>;

const createOp: Operation<void, void, CreateWorkflowBody, { ok: boolean; slug?: string; error?: string }> = {
  method: "POST",
  path: () => "/api/workflows/create",
  body: createBody,
  response: createResult,
  // `createLoopAction` → `revalidatePath("/agents/workflows")`. A bare loop row
  // with no steps: the fleet gains a row, and runnability's `total` (and
  // therefore `runnable`) moves.
  invalidates: [workflowFleet.list, loopsRunnability.get],
};

export const workflowBuilder = {
  propose: proposeOp,
  createFromBuilder: createFromBuilderOp,
  create: createOp,
};
